// Lightweight bearer attacher for server-fn calls.
//
// Reads the Supabase session directly from localStorage instead of importing
// the full `@supabase/supabase-js` browser client. Importing the browser
// client into `src/start.ts` pulls Realtime (Phoenix + WebSocket) into the
// main bundle (~60-80KB gzip). This file avoids that entirely.
//
// Storage layout (as of supabase-js v2): under key `sb-<project-ref>-auth-token`
// the value is either the JSON-stringified session, or (when large) split
// into chunks `sb-<ref>-auth-token.0`, `.1`, ... with the base key holding
// a manifest.

import { createMiddleware } from "@tanstack/react-start";

const PROJECT_REF = import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined;
const STORAGE_KEY = PROJECT_REF ? `sb-${PROJECT_REF}-auth-token` : "";

function readStoredSession(): { access_token?: string } | null {
  if (typeof window === "undefined" || !STORAGE_KEY) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    // Chunked layout: base value is a JSON array of chunk keys.
    if (raw.startsWith("[")) {
      const parts = JSON.parse(raw) as string[];
      const joined = parts
        .map((k) => window.localStorage.getItem(k) ?? "")
        .join("");
      return JSON.parse(joined) as { access_token?: string };
    }
    return JSON.parse(raw) as { access_token?: string };
  } catch {
    return null;
  }
}

export const attachSupabaseAuthLite = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    const session = readStoredSession();
    const token = session?.access_token;
    return next({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
);
